/**
 * @module types/cart
 * @description Cart and territory-related type definitions for UI/frontend
 *
 * NOTE: These types are UI-specific, simplified versions for frontend cart display.
 * For backend/lambda operations, use Sanity.Types.Cart<T> which includes invoice fields
 * and full order data. The types in this file are intentionally kept separate to avoid
 * coupling the frontend cart UI to backend order/invoice logic.
 */

/**
 * @interface Territory
 * @description Territory entry with value, label, and numeric ID
 */
export interface Territory {
  value: string;
  label: string;
  id: number;
}

/**
 * @enum DiscountType
 * @description Type of discount applied to cart
 */
export enum DiscountType {
  PERCENT = 'percent',
  AMOUNT = 'amount',
}

/**
 * @interface CartDiscount
 * @description Discount applied to cart
 */
export interface CartDiscount {
  code: string;
  name: string;
  deduction: number;
  type: DiscountType;
  id?: string;
}

/**
 * @interface CartItem
 * @description Item in shopping cart
 */
export interface CartItem {
  _id: string;
  price: number;
  [key: string]: unknown;
}

/**
 * @interface Cart
 * @description Shopping cart data structure
 */
export interface Cart {
  items: CartItem[];
  total: number;
  discount: CartDiscount | null;
}
